import React, { useContext, useMemo } from 'react';
import {
  Input,
  Checkbox,
  Form,
  UploadFile,
  Row,
  Col,
  DatePicker,
  FormInstance,
  Divider,
  Radio,
  Select,
} from 'antd';
import { useTranslation } from 'react-i18next';
import ImageUpload from '@components/ImageUpload';
import moment from 'moment';
import SelectCustomers from '@components/SelectCustomers';
import { ProductsConstants } from '@utils/applicationsConstants';
import SessionContext from '@providers/Session/SessionContext';
import { disabledTime } from '@utils/disabledTime';

interface BannerConfigProps {
  defaultFile?: UploadFile<any>;
  form: FormInstance<any>;
}

const BannerConfig = ({ defaultFile, form }: BannerConfigProps) => {
  const { t } = useTranslation(['banners', 'main']);
  const { isRoot } = useContext(SessionContext);
  const startDate = Form.useWatch('startDate', form);
  const recipientType = Form.useWatch('recipientType', form);
  const dimensions = useMemo(() => ({ width: 1200, height: 400 }), []);

  const customerTypesOptions = useMemo(
    () =>
      ['ALL', 'BROKER', 'CUSTOMER'].map(type => ({
        label: t(`columns.recipients.types.${type}`),
        value: type,
      })),
    [t],
  );

  const productsOptions = useMemo(
    () => [
      {
        label: t('columns.recipients.types.ALL'),
        value: 'ALL',
      },
      ...Object.values(ProductsConstants).map(product => ({
        label: t(`main:apps.${product}`),
        value: product?.toUpperCase(),
      })),
    ],
    [t],
  );

  return (
    <>
      <Form.Item
        name="description"
        label={t('modal.labels.description')}
        style={{ marginBottom: 16 }}
        rules={[
          {
            required: true,
            message: t('msgs.required'),
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Row gutter={[16, 16]}>
        <Col sm={12} xs={24}>
          <Form.Item name="startDate" label={t('modal.labels.start-date')}>
            <DatePicker
              placeholder=""
              style={{ width: '100%' }}
              format="DD/MM/YYYY, HH:mm"
              showTime={{ format: 'HH:mm' }}
              disabledDate={current => {
                return current && current < moment().startOf('day');
              }}
              disabledTime={disabledTime}
            />
          </Form.Item>
        </Col>
        <Col sm={12} xs={24}>
          <Form.Item
            name="expirationDate"
            label={t('modal.labels.expiration-date')}
          >
            <DatePicker
              placeholder=""
              style={{ width: '100%' }}
              format="DD/MM/YYYY, HH:mm"
              showTime={{ format: 'HH:mm' }}
              disabledDate={current => {
                return (
                  current &&
                  (current < moment().startOf('day') || current < startDate)
                );
              }}
            />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item
        name="file"
        style={{ margin: '8px 0' }}
        rules={[
          {
            required: true,
            message: t('msgs.required'),
          },
        ]}
        label={t('modal.labels.image')}
      >
        <ImageUpload
          dimensions={dimensions}
          defaultFile={defaultFile}
          allowCut
        />
      </Form.Item>
      <Divider
        style={{
          margin: '24px 0',
          fontSize: '0.8rem',
          opacity: 0.6,
        }}
      >
        {t('modal.labels.recipients')}
      </Divider>
      {isRoot && (
        <>
          <Row justify="center">
            <Form.Item name="recipientType">
              <Radio.Group
                onChange={({ target: { value } }) => {
                  form?.setFieldValue('recipientType', value);
                }}
              >
                <Radio.Button value="type">
                  {t('modal.labels.by-type')}
                </Radio.Button>
                <Radio.Button value="customer">
                  {t('modal.labels.selected')}
                </Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Row>
          {recipientType === 'type' && (
            <Row gutter={[16, 16]}>
              <Col sm={12} xs={24}>
                <Form.Item name="type" label={t('modal.labels.customer-type')}>
                  <Select options={customerTypesOptions} allowClear />
                </Form.Item>
              </Col>
              <Col sm={12} xs={24}>
                <Form.Item
                  name="product"
                  label={t('modal.labels.product-type')}
                >
                  <Select options={productsOptions} allowClear />
                </Form.Item>
              </Col>
            </Row>
          )}
        </>
      )}
      {(!isRoot || recipientType === 'customer') && (
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Form.Item name="customers">
              <SelectCustomers mode="multiple" allowUpload />
            </Form.Item>
          </Col>
        </Row>
      )}

      <Form.Item name="addInfoEnabled" valuePropName="checked">
        <Checkbox>{t('modal.enabled-extra-info')}</Checkbox>
      </Form.Item>
    </>
  );
};

export default BannerConfig;
